import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  cn,
  displayPrice,
  getImageUrl,
  getMenuItemDisplayPrice,
} from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { useAppSelector } from "@/redux/features/hooks";
import { useDeleteMenuItemMutation } from "@/redux/services/menu/menu.api";
import { MenuItem } from "@/redux/services/menu/menu.type";
import MenuItemIngredients from "@/components/inventory/recipes/MenuItemIngredients";
import { ChevronDown, Pencil, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

const Section = ({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section>
        <CollapsibleTrigger className="w-full flex items-center justify-between py-2 border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-card-foreground transition-colors">
          <span>{title}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              open && "rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 space-y-3">
          {children}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
};

const MetaRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-card-foreground font-medium text-right">{value}</span>
  </div>
);

interface Props {
  item: MenuItem | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (item: MenuItem) => void;
  onAfterDelete?: () => void;
}

const MenuItemDetailsDrawer = ({
  item,
  isOpen,
  onClose,
  onEdit,
  onAfterDelete,
}: Props) => {
  const currency = useRestaurantCurrency();
  const confirm = useConfirm();
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  // Recipes follow the API's inventory permissions (SFRESTO-136): the
  // section shows on inventory.view; editing needs inventory.manage, which
  // the recipe editor checks itself.
  const { can } = usePermissions();
  const canViewRecipe = can("inventory.view");

  const [deleteMenuItem, { isLoading: isDeleting }] =
    useDeleteMenuItemMutation();

  const handleDelete = async () => {
    if (!item) return;
    const ok = await confirm({
      title: `Supprimer "${item.product?.title ?? item.title}" ?`,
      description: "Cette action est irréversible.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      await deleteMenuItem(item.id).unwrap();
      toast.success("Élément supprimé");
      onAfterDelete?.();
      onClose();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const displayedPrice = item ? getMenuItemDisplayPrice(item) : 0;

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
          <SheetTitle className="text-base">
            {item
              ? item.product?.title ?? item.title
              : "Détails de l'élément"}
          </SheetTitle>
          <SheetDescription hidden>
            Détails et actions pour cet élément de menu.
          </SheetDescription>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {item ? (
            <>
              <Section title="Aperçu">
                {(item.product?.image?.url ?? item.image?.url) && (
                  <div className="w-32 h-32 bg-muted/50 rounded-full overflow-hidden border-2 border-border">
                    <img
                      src={getImageUrl(
                        item.product?.image?.url ?? item.image?.url
                      )}
                      alt={item.product?.title ?? item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <MetaRow
                    label="Prix"
                    value={
                      <span className="text-primary font-semibold">
                        {displayPrice(displayedPrice, currency)}
                      </span>
                    }
                  />
                  <MetaRow label="Position" value={item.sortOrder} />
                </div>
                {(item.product?.description ?? item.description) && (
                  <p className="text-sm text-muted-foreground bg-muted/50 rounded-md p-3">
                    {item.product?.description ?? item.description}
                  </p>
                )}
                <span
                  className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                    item.isAvailable
                      ? "bg-accent-light text-accent"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {item.isAvailable ? "Disponible" : "Indisponible"}
                </span>
              </Section>

              {item.itemOptions && item.itemOptions.length > 0 && (
                <Section title={`Options (${item.itemOptions.length})`}>
                  <ul className="space-y-3">
                    {item.itemOptions.map((option) => (
                      <li
                        key={option.id}
                        className="border border-border rounded-md p-3 bg-muted/30"
                      >
                        <p className="font-medium text-sm">
                          {option.menuItemOption.title}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                          <span className="px-1.5 py-0.5 rounded bg-card text-muted-foreground border border-border">
                            Max {option.maxSelections}
                          </span>
                          {option.isRequired && (
                            <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                              Requis
                            </span>
                          )}
                        </div>
                        {option.menuItemOption.menuItemOptionValues &&
                          option.menuItemOption.menuItemOptionValues.length >
                            0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {option.menuItemOption.menuItemOptionValues.map(
                                (v) => (
                                  <span
                                    key={v.id}
                                    className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                                  >
                                    {v.title}
                                  </span>
                                )
                              )}
                            </div>
                          )}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {canViewRecipe && restaurant && item.id && (
                <Section title="Ingrédients" defaultOpen={false}>
                  <MenuItemIngredients
                    menuItemId={item.id}
                    restaurantId={restaurant.id}
                  />
                </Section>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Impossible de charger les détails.
            </p>
          )}
        </div>

        <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!item || isDeleting}
          >
            <Trash2 className="h-4 w-4" />
            Supprimer
          </Button>
          <Button
            type="button"
            variant="accent"
            onClick={() => item && onEdit(item)}
            disabled={!item}
          >
            <Pencil className="h-4 w-4" />
            Modifier
          </Button>
        </footer>
      </SheetContent>
    </Sheet>
  );
};

export default MenuItemDetailsDrawer;
